import { deriveComicTitle, type Comic, type ComicKind } from '@tanko/shared'
import { db, type Library, type LocalUploadSession, type StoredCollection, type StoredCollectionMember } from './schema'
import { newQueueEntry, type BackupQueueEntry, type BackupQueueStatus } from '../lib/backup-queue-entry'
import type { QueueReconciliation } from '../lib/backup-queue'

export function comicId(libraryId: string, relPath: string): string {
  return JSON.stringify([libraryId, relPath])
}

/**
 * relPath 是作品相對於來源的路徑；kind 為 comic 的來源其唯一一本作品為空字串。
 * name 只用來推導預設書名與作者，與識別碼無關。
 */
/**
 * fallbackName 是相對路徑為空時（直接匯入的單一作品資料夾）用來推導標題的名稱，
 * 傳來源自己的名稱即可——那就是使用者選的那個資料夾名。沒有它的話標題會是空字串。
 * 識別碼仍由來源識別碼與空相對路徑組成，不受這個值影響。
 */
export async function addComics(
  library: Library,
  folders: { relPath: string; kind: ComicKind; pageCount: number }[],
  fallbackName = '',
): Promise<Comic[]> {
  const comics: Comic[] = folders.map(({ relPath, kind, pageCount }) => ({
    id: comicId(library.id, relPath), libraryId: library.id, relPath, kind,
    ...deriveComicTitle(relPath || fallbackName, kind), pageCount, indexed: false, coverPage: 0,
    cloudComicId: null, syncState: 'unregistered', addedAt: Date.now(),
  }))
  return db.transaction('rw', db.comics, async () => {
    const added: Comic[] = []
    for (const comic of comics) {
      if (!await db.comics.get(comic.id)) {
        await db.comics.add(comic)
        added.push(comic)
      }
    }
    return added
  })
}

export async function removeComic(id: string): Promise<void> {
  await db.transaction('rw', [db.comics, db.pageMeta, db.comicCategories, db.readingProgress, db.bookmarks, db.coverThumbnails, db.uploadSessions, db.backupQueue], async () => {
    await db.readingProgress.delete(id)
    await db.bookmarks.where('comicId').equals(id).delete()
    await db.coverThumbnails.delete(id)
    await db.uploadSessions.delete(id)
    // 佇列項在同一次移除中收掉，不留下要等下一次排程才清除的孤兒。
    await db.backupQueue.delete(id)
    await db.comics.delete(id)
    await db.pageMeta.where('comicId').equals(id).delete()
    await db.comicCategories.where('comicId').equals(id).delete()
  })
}

/**
 * 移除一個來源與它底下的全部作品。硬碟上的檔案不受影響。
 * 作品識別碼由來源識別碼與相對路徑組成，來源沒了就再也解析不到目錄，
 * 因此不保留孤兒作品。
 */
export async function removeLibrary(libraryId: string): Promise<number> {
  return db.transaction('rw',
    [db.libraries, db.comics, db.pageMeta, db.comicCategories, db.readingProgress, db.bookmarks, db.coverThumbnails, db.uploadSessions, db.backupQueue],
    async () => {
      const owned = await db.comics.where('libraryId').equals(libraryId).toArray()
      for (const comic of owned) {
        await db.readingProgress.delete(comic.id)
        await db.bookmarks.where('comicId').equals(comic.id).delete()
        await db.coverThumbnails.delete(comic.id)
        await db.uploadSessions.delete(comic.id)
        await db.backupQueue.delete(comic.id)
        await db.pageMeta.where('comicId').equals(comic.id).delete()
        await db.comicCategories.where('comicId').equals(comic.id).delete()
        await db.comics.delete(comic.id)
      }
      await db.libraries.delete(libraryId)
      return owned.length
    })
}

export async function saveUploadSession(session: LocalUploadSession): Promise<void> {
  await db.uploadSessions.put(session)
}

/**
 * 來源重新指向：只換 handle 與顯示名稱，識別碼不動。
 *
 * 來源識別碼是底下每一本作品識別碼的前半段，換掉它等於整個來源全部改鍵；handle 只是
 * 「這個識別碼目前指向硬碟上哪裡」，更換 handle 就是重新指向的本義。硬碟上什麼都不動。
 */
export async function relocateLibrary(libraryId: string, handle: FileSystemDirectoryHandle): Promise<void> {
  const updated = await db.libraries.update(libraryId, { handle, name: handle.name })
  if (!updated) throw new Error('找不到這個來源，無法重新指向。')
}

/** 依 comicId 鍵入的七張從屬表；改鍵時每一張都要跟著走。 */
const dependentTables = () => [
  db.pageMeta, db.comicCategories, db.readingProgress, db.bookmarks, db.coverThumbnails, db.uploadSessions, db.backupQueue,
] as const

/**
 * 作品重新指向：在單一交易內把識別碼改成由新來源與新相對路徑推導的值，七張從屬表一併改鍵。
 *
 * 不採用「保留舊識別碼、只改 relPath」：加入作品以識別碼判斷是否已加入，舊路徑之後若出現
 * 另一本會被靜默當成「已加入」；識別碼由路徑決定是既有規則，改鍵才與它一致。標題、作者、
 * 封面頁、雲端識別碼、同步狀態與加入時間原封不動。交易內只碰 Dexie 的表，不碰檔案系統，
 * 否則 Dexie 會在 await 非 Dexie 的 Promise 時提前提交。
 */
export async function relinkComic(id: string, target: { libraryId: string; relPath: string }): Promise<Comic> {
  const nextId = comicId(target.libraryId, target.relPath)
  return db.transaction('rw', [db.comics, ...dependentTables()], async () => {
    const comic = await db.comics.get(id)
    if (!comic) throw new Error('作品已不在書庫裡，無法更新位置。')
    if (nextId !== id && await db.comics.get(nextId)) {
      throw new Error(`這個位置已經是另一本作品，無法把「${comic.title}」指向它。`)
    }
    const relinked: Comic = { ...comic, id: nextId, libraryId: target.libraryId, relPath: target.relPath }
    for (const table of dependentTables()) {
      const rows = await table.where('comicId').equals(id).toArray()
      if (!rows.length) continue
      await table.where('comicId').equals(id).delete()
      // 各表的主鍵都含 comicId（或就是 comicId），換掉它就是換主鍵，因此先刪後加而非 update。
      await (table as { bulkAdd(items: unknown[]): Promise<unknown> }).bulkAdd(rows.map(row => ({ ...row, comicId: nextId })))
    }
    await db.comics.delete(id)
    await db.comics.add(relinked)
    return relinked
  })
}

/**
 * 為尚未註冊的作品配發雲端目錄識別碼並持久化。
 *
 * 識別碼必須在第一個網路請求之前寫進 IndexedDB：註冊因此冪等，逾時、關閉分頁或
 * 重新載入都能以同一個識別碼重試，不會在雲端留下無法從本機指認的第二筆。
 * 這一步不改 syncState——配發完仍是 unregistered，註冊成功才轉為 pending。
 */
export async function assignCatalogIds(ids: readonly string[]): Promise<void> {
  if (!ids.length) return
  await db.transaction('rw', db.comics, async () => {
    for (const id of ids) {
      const comic = await db.comics.get(id)
      if (comic && !comic.cloudComicId) await db.comics.update(id, { cloudComicId: crypto.randomUUID() })
    }
  })
}

/** 整批註冊成功後才標記；未成功的維持 unregistered，下一輪重試。 */
export async function markCatalogRegistered(ids: readonly string[]): Promise<void> {
  if (!ids.length) return
  await db.transaction('rw', db.comics, async () => {
    for (const id of ids) {
      const comic = await db.comics.get(id)
      // 已經 ready 的不退回 pending：發布成功的狀態不該被一次補註冊蓋掉。
      if (comic && comic.syncState === 'unregistered') await db.comics.update(id, { syncState: 'pending' })
    }
  })
}

export async function bindCloudComic(comicId: string, uploadId: string, cloudComicId: string): Promise<void> {
  await db.transaction('rw', [db.comics, db.uploadSessions], async () => {
    const [comic, session] = await Promise.all([db.comics.get(comicId), db.uploadSessions.get(comicId)])
    if (!comic) throw new Error('作品已被移除，無法寫回雲端綁定。')
    if (!session || session.uploadId !== uploadId) throw new Error('本機上傳紀錄已變更，無法寫回雲端綁定。')
    // 備份完成才轉為 ready；cloudComicId 在註冊時就已寫入，這裡只確認它指向同一筆。
    await db.comics.update(comicId, { cloudComicId, syncState: 'ready' })
    await db.uploadSessions.delete(comicId)
  })
}

/**
 * 綁在某個雲端目錄項上、尚未發布的本機上傳 session。
 *
 * 伺服器端的 upload_sessions 在發布之前不記錄它屬於哪一筆目錄項（comic_id 要到發布才寫），
 * 因此刪除一個 pending 作品時，伺服器推不出該清哪個 R2 prefix，已上傳的分片與物件會留到
 * 七天 TTL 才被清掉。知道這個對應關係的只有本機這張表，所以由這一端負責取消。
 *
 * 發布成功時綁定會刪掉 session 紀錄，因此這裡只會找到未完成的那些。
 */
export async function pendingUploadsFor(cloudComicId: string): Promise<{ comicId: string; uploadId: string }[]> {
  return db.transaction('r', [db.comics, db.uploadSessions], async () => {
    const bound = await db.comics.filter(comic => comic.cloudComicId === cloudComicId).toArray()
    const sessions = await Promise.all(bound.map(comic => db.uploadSessions.get(comic.id)))
    return sessions
      .filter((session): session is NonNullable<typeof session> => !!session)
      .map(session => ({ comicId: session.comicId, uploadId: session.uploadId }))
  })
}

/** 手動刪除雲端副本後留在佇列項上的原因。 */
export const CLOUD_COPY_DELETED_REASON = '已手動從雲端刪除'

/**
 * 雲端目錄項刪除後解除本地綁定。本地原始資料夾、進度、書籤與分類都保留，
 * 作品回到未註冊狀態，之後可以重新註冊。
 *
 * 同時在備份佇列留下一筆跳過紀錄。解除綁定會讓作品變回未備份，而佇列的自我修復規則
 * 會把「已完成但作品不是已備份」的項目排回等待中——那條規則分不出雲端副本是意外不見
 * 還是被刻意刪掉，沒有這筆紀錄的話，下一次按開始備份就會把剛刪掉的那本又傳上去。
 */
export async function unbindCloudComic(cloudComicId: string): Promise<string[]> {
  return db.transaction('rw', [db.comics, db.backupQueue], async () => {
    const bound = await db.comics.filter(comic => comic.cloudComicId === cloudComicId).toArray()
    const now = Date.now()
    for (const comic of bound) {
      await db.comics.update(comic.id, { cloudComicId: null, syncState: 'unregistered' })
      const entry = await db.backupQueue.get(comic.id)
      await db.backupQueue.put({
        ...(entry ?? newQueueEntry(comic.id, now)),
        status: 'skipped', reason: CLOUD_COPY_DELETED_REASON, updatedAt: now,
      })
    }
    return bound.map(comic => comic.id)
  })
}

export async function clearUploadSession(comicId: string, uploadId: string): Promise<void> {
  await db.transaction('rw', db.uploadSessions, async () => {
    const session = await db.uploadSessions.get(comicId)
    if (session?.uploadId === uploadId) await db.uploadSessions.delete(comicId)
  })
}

/**
 * 以雲端目錄回傳的分類整份取代本地鏡像。
 *
 * 分類的真相來源是雲端；本地這兩張表只是為了在認證失效或離線時仍能顯示分類名稱
 * 與篩選本地作品。整份取代而非逐筆比對，因為雲端回傳的就是完整清單。
 */
export async function mirrorCategories(categories: readonly { id: string; name: string; private?: boolean }[]): Promise<void> {
  await db.transaction('rw', db.categories, db.comicCategories, async () => {
    const ids = new Set(categories.map(category => category.id))
    await db.categories.clear()
    await db.categories.bulkAdd(categories.map((category, index) => ({ id: category.id, name: category.name, sortOrder: index, private: !!category.private })))
    // 指向已不存在分類的指派一併清掉，鏡像才不會留下孤兒列。
    const orphans = (await db.comicCategories.toArray()).filter(entry => !ids.has(entry.categoryId))
    await db.comicCategories.bulkDelete(orphans.map(entry => [entry.comicId, entry.categoryId] as [string, string]))
  })
}

/**
 * 整份取代集合鏡像。成員與分類指派一併重建，因此不會留下指向已解散集合的孤兒列。
 */
export type MirroredCollection = StoredCollection & { categoryIds: string[]; memberCount: number; members: Omit<StoredCollectionMember, 'collectionId'>[] }

export async function mirrorCollections(items: readonly MirroredCollection[]): Promise<void> {
  await db.transaction('rw', db.collections, db.collectionMembers, db.collectionCategories, async () => {
    await Promise.all([db.collections.clear(), db.collectionMembers.clear(), db.collectionCategories.clear()])
    await db.collections.bulkAdd(items.map(({ id, title, mode, coverComicId, createdAt, updatedAt }) => ({ id, title, mode, coverComicId, createdAt, updatedAt })))
    await db.collectionMembers.bulkAdd(items.flatMap(item => item.members.map(member => ({
      collectionId: item.id, comicId: member.comicId, position: member.position, title: member.title, pageCount: member.pageCount,
      coverPage: member.coverPage, syncState: member.syncState, progress: member.progress,
    }))))
    await db.collectionCategories.bulkAdd(items.flatMap(item => item.categoryIds.map(categoryId => ({ collectionId: item.id, categoryId }))))
  })
}

/** 離線或登入過期時用鏡像折出集合格。與 API 回應同形，成員依 position 排序。 */
export async function readMirroredCollections(): Promise<MirroredCollection[]> {
  const [collections, members, categories] = await Promise.all([db.collections.toArray(), db.collectionMembers.toArray(), db.collectionCategories.toArray()])
  return collections.map(collection => {
    const own = members.filter(member => member.collectionId === collection.id).sort((a, b) => a.position - b.position)
      .map(({ collectionId: _collectionId, ...member }) => member)
    return {
      ...collection, categoryIds: categories.filter(item => item.collectionId === collection.id).map(item => item.categoryId),
      memberCount: own.length, members: own,
    }
  })
}

/**
 * 取代一本作品在鏡像中的分類指派。
 *
 * comicId 是本地識別碼。純雲端的作品（本地沒有檔案的那些）在 comics 沒有對應列，
 * 鏡像因此無處可寫也不需要寫——書庫的雲端範圍直接讀雲端資料。
 */
export async function mirrorComicCategories(comicId: string, categoryIds: readonly string[]): Promise<void> {
  await db.transaction('rw', db.comics, db.comicCategories, async () => {
    if (!await db.comics.get(comicId)) return
    await db.comicCategories.where('comicId').equals(comicId).delete()
    await db.comicCategories.bulkAdd([...new Set(categoryIds)].map(categoryId => ({ comicId, categoryId })))
  })
}

/**
 * 批次指派的鏡像：整批在**一個**交易內取聯集或差集並寫回。
 *
 * 書庫 store 以 liveQuery 訂閱這兩張表，每個交易提交都會讓整份清單重新發出；逐本各開
 * 一個交易，二十本就是二十次重新發出，畫面上每張封面跟著清空重載二十次。合成一個交易
 * 之後只發出一次。規則與單本相同：加入只加、移除只移，沒被指名的指派不動；純雲端作品在
 * comics 沒有對應列，鏡像無處可寫也不需要寫。
 */
export async function mirrorAssignmentsBatch(
  comics: readonly Pick<Comic, 'id'>[], categoryIds: readonly string[], mode: 'add' | 'remove',
): Promise<void> {
  const wanted = [...new Set(categoryIds)]
  if (!comics.length || !wanted.length) return
  await db.transaction('rw', db.comics, db.comicCategories, async () => {
    const present = (await db.comics.bulkGet(comics.map(comic => comic.id))).flatMap(comic => comic ? [comic.id] : [])
    if (!present.length) return
    const existing = await db.comicCategories.where('comicId').anyOf(present).toArray()
    if (mode === 'remove') {
      const doomed = existing.filter(entry => wanted.includes(entry.categoryId))
      await db.comicCategories.bulkDelete(doomed.map(entry => [entry.comicId, entry.categoryId] as [string, string]))
      return
    }
    const held = new Set(existing.map(entry => `${entry.comicId}\n${entry.categoryId}`))
    const rows = present.flatMap(comicId => wanted.map(categoryId => ({ comicId, categoryId })))
      .filter(row => !held.has(`${row.comicId}\n${row.categoryId}`))
    if (rows.length) await db.comicCategories.bulkAdd(rows)
  })
}

/**
 * 把本地指派的分類識別碼改寫為雲端識別碼。
 *
 * 必須在整份取代 categories 鏡像之前執行：鏡像換成雲端識別碼之後，還指著本地
 * 識別碼的指派會被當成孤兒清掉，等於把使用者的分類指派靜默刪除。
 */
export async function adoptCloudCategoryIds(byComic: ReadonlyMap<string, string[]>): Promise<void> {
  await db.transaction('rw', db.comicCategories, async () => {
    await db.comicCategories.clear()
    const rows = [...byComic].flatMap(([comicId, categoryIds]) => categoryIds.map(categoryId => ({ comicId, categoryId })))
    await db.comicCategories.bulkAdd(rows)
  })
}

/** 只寫本地的標題與作者鏡像；分類由 mirrorComicCategories 負責。純雲端作品無列可更新。 */
export async function mirrorComicMetadata(comicId: string, title: string, author: string): Promise<void> {
  await db.comics.update(comicId, { title, author })
}

/** 備份佇列全部項目。筆數與書庫作品數同級（數百），一次讀進來即可。 */
export async function loadBackupQueue(): Promise<BackupQueueEntry[]> {
  return db.backupQueue.toArray()
}

/** 套用一次排程結果。整批在同一個交易內完成，避免中途失敗留下半套佇列。 */
export async function applyQueueReconciliation(result: QueueReconciliation): Promise<void> {
  if (!result.upserts.length && !result.removedIds.length) return
  await db.transaction('rw', db.backupQueue, async () => {
    if (result.removedIds.length) await db.backupQueue.bulkDelete([...result.removedIds])
    if (result.upserts.length) await db.backupQueue.bulkPut([...result.upserts])
  })
}

/**
 * 記錄一筆佇列項目的狀態。每一步都立即寫回，中斷之後才看得出做到哪裡與為什麼停。
 * 項目已被移除（作品刪掉了）時靜默忽略：那不是錯誤，重建交易只會讓它復活。
 */
export async function recordQueueStatus(
  comicId: string, status: BackupQueueStatus, reason: string,
): Promise<void> {
  await db.transaction('rw', db.backupQueue, async () => {
    const entry = await db.backupQueue.get(comicId)
    if (!entry) return
    await db.backupQueue.put({
      ...entry, status, reason, updatedAt: Date.now(),
      attempts: status === 'running' ? entry.attempts + 1 : entry.attempts,
    })
  })
}

/**
 * 明確加入佇列。已在佇列裡的沿用原本的排入時間，只把非待處理的狀態拉回 queued。
 * `queuedAt` 同時是執行順序，因此插隊只要給一個比目前所有待處理項都小的值。
 */
export async function enqueueComics(ids: readonly string[], queuedAt: number): Promise<void> {
  if (!ids.length) return
  await db.transaction('rw', db.backupQueue, async () => {
    const existing = await db.backupQueue.bulkGet([...ids])
    await db.backupQueue.bulkPut(ids.map((id, index) => {
      const entry = existing[index]
      if (!entry) return newQueueEntry(id, queuedAt)
      return { ...entry, status: 'queued' as const, reason: '', queuedAt, updatedAt: Date.now() }
    }))
  })
}

/** 把失敗的項目放回等待中，供「重試失敗的」使用。回傳被放回的筆數。 */
export async function requeueFailed(): Promise<number> {
  return db.transaction('rw', db.backupQueue, async () => {
    const failed = await db.backupQueue.where('status').equals('failed').toArray()
    if (failed.length) {
      const now = Date.now()
      await db.backupQueue.bulkPut(failed.map(entry => ({ ...entry, status: 'queued' as const, reason: '', updatedAt: now })))
    }
    return failed.length
  })
}
